import { Component, inject, Input, ViewChild, ViewEncapsulation } from '@angular/core';
import { Router } from "@angular/router";
import { ENV } from "@bequeathed/ngx-app-core";
import { AIService, InterviewService, MessageService, Question } from "@bequeathed/ngx-services";
import { Topic } from "@bequeathed/ngx-services/lib/model/ai-interview.model";
import { SummaryComponent } from "../../../../features/interview/summary/summary.component";
import { QuestionUtils } from "../../../utils/question.utils";
import { EditQuestionPopupComponent } from "../../edit-question-popup/edit-question-popup.component";

/**
 * Displays questions as read-only on the summary page
 *
 * @author Dan Bennett (dbennett)
 */
@Component({
    selector: 'summary-questions',
    templateUrl: './summary-questions.component.html',
    styleUrls: ['./summary-questions.component.sass'],
    encapsulation: ViewEncapsulation.None
})
export class SummaryQuestionsComponent {

    // @ts-expect-error Provided via template
    @Input() type: string;
    // @ts-expect-error Provided via template
    @Input() topic: Topic;
    @Input() interviewId: number | undefined;
    @Input() questions: Question[] = [];
    @Input() active = false;
    @Input() loading = false;
    // @ts-expect-error Provided via template
    @Input() summary: SummaryComponent;
    // @ts-expect-error Provided via template
    @Input() onUpdated: () => void;

    router: Router = inject(Router)
    // @ts-expect-error it's in the template
    @ViewChild('editQuestionPopup') editQuestionPopup: EditQuestionPopupComponent
    // @ts-expect-error it's in the template
    @ViewChild('editConfirm') editConfirm: EditConfirmationDialogComponent

    public messages = inject(MessageService);
    private interviewService = inject(InterviewService);
    private aiService = inject(AIService);
    DEBUG = ENV.config.get("show-debug") === "true" || ENV.config.get("show-debug") === true

    /**
     * Feature flag guarding the mid-interview backward-navigation edit flow.
     *
     * Read from the runtime config map (`ENV.config`), mirroring the getter in
     * `NavigatorComponent` / `ChatInterfaceComponent`. When false/absent, the
     * summary page keeps its existing edit behaviour (inline popup for normal
     * questions, navigate-into-interview for GROUP questions).
     */
    get backwardNavigationEnabled(): boolean {
        return ENV.config.get('backward-navigation-enabled') === true ||
            ENV.config.get('backward-navigation-enabled') === 'true';
    }

    /**
     * Helper method that allows the "standard" text for a question to be overridden by a summary version
     *
     * @param question
     */
    getMessage(question: Question): string {
        // ToDo :: Use QuestionUtils instead
        const id = question.id!.replace(' ', '').toLowerCase();
        // Check for summary specific first
        let message = this.messages.getText(`${this.type}.summary.${id}`);
        if (message) {
            return message;
        }
        message = this.text(question) || '';
        return this.sanitise(message);
    }

    private sanitise(htmlMessage: string): string {
        return htmlMessage
            .replace(/( |)<\/(p|div|li)>( |)/g, "\n")
            .replace(/( |)<\/li>( |)/g, "\n - ")
            .replace(/<(\/|)(li|ol|b|strong|p(\sclass="field-label"|)|div(\sclass="field-description"|))>/g, "");
    }

    text(question: Question): string | null {
        return QuestionUtils.getText(this.type, question, this.messages, null, true);
    }

    getAnswer(question: Question): string {
        return QuestionUtils.getAnswer(this.type, question, this.messages, null, true);
    }

    async editQuestion(question: Question, topic: Topic) {
        // Flag ON: unified flow for ALL question types (incl. GROUP). Show the
        // pre-edit confirmation and, on confirm, navigate INTO the interview to
        // edit. The ChatInterfaceComponent editComplete init handles the actual
        // edit-load on landing, so we only navigate here (no pre-call).
        if (this.backwardNavigationEnabled) {
            const originatingElement = document.activeElement instanceof HTMLElement
                ? document.activeElement
                : undefined;
            const confirmed = (await this.editConfirm.open({ originatingElement })) === true;
            if (!confirmed) {
                // Dismissed: no state change, stay on the summary.
                return;
            }
            this.interviewService.summaryState.editing = true;
            this.interviewService.summaryState.editingQuestion = question.id;
            await this.router.navigate([`interview/WILL/${this.interviewId}`], {
                queryParams: {
                    edit: true,
                    topicId: topic.id,
                    questionId: question.id
                }
            });
            return;
        }

        // Flag OFF: existing behaviour, unchanged.
        if (question.type?.includes('GROUP')) {
            await this.editGroupQuestion(question, topic)
            return
        }
        return this.editQuestionPopup.open({
            question: question,
            topic: topic,
            confirmButtonLabel: this.messages.getText('WILL.button.save-and-continue'),
            dismissButtonLabel: this.messages.getText('btn.exit')
        })

    }

    async editGroupQuestion(question: Question, topic: Topic) {
        const userId = this.summary!.sessionId!.split(`-`)[0]

        await this.aiService.editInterviewQuestion(
            String(this.interviewId!),
            userId,
            false,
            Object.entries({ editTopicId: topic.id, editQuestionId: question.id })
        );

        this.interviewService.summaryState.editing = true;
        this.interviewService.summaryState.editingQuestion = question.id;
        await this.router.navigate([`interview/WILL/${this.interviewId}`], {
            queryParams: {
                edit: true,
                topicId: topic.id,
                questionId: question.id
            }
        })
    }
}
